import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/common";
import { useResource } from "@/hooks/use-resource";
import { useMediaQuery } from "@/hooks/use-media-query";
import { longDate } from "@/lib/sk";
import { SectionHead } from "@/components/canvas/SectionHead";
import { Chips, PageBar, QuietSearch, Segment } from "@/components/layout/PageBar";
import { RateFamilies, LineRows } from "@/components/produkter/LineRows";
import { LineSheet } from "@/components/produkter/LineSheet";
import { AreasView } from "@/components/produkter/Views";
import { KIND_FILTERS, VIEWS, matchKind, matchLine } from "@/lib/produkter";

const SECTIONS_KEY = "sk.produkter.sections";
const readSections = () => { try { return JSON.parse(localStorage.getItem(SECTIONS_KEY) || "{}"); } catch { return {}; } };

function groupSummary(g) {
  if (g.key === "timepriser") return `${g.families} tjenester · ${g.count} satser`;
  const words = g.priced === g.count ? "fast pris" : g.priced ? `${g.priced} med fast pris · ${g.count - g.priced} pr. time eller etter regning` : "pr. time eller etter regning";
  return `${g.count} ${g.count === 1 ? "linje" : "linjer"} · ${words}`;
}

/** Produkter og tjenester: what Skarpenord delivers, the way it is priced. Read-only; every number carries its source. */
export default function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const { data } = useResource("/produkter", null);
  const mobile = !useMediaQuery("(min-width: 768px)");
  const view = params.get("view") || "prisliste";
  const g = params.get("g") || "all";
  const kind = params.get("kind") || "alle";
  const q = params.get("q") || "";
  const lineId = params.get("l");
  const fag = params.get("fag");
  const [sections, setSections] = useState(readSections);
  const searchRef = useRef(null);
  const setParam = (patch) => setParams((prev) => { const n = new URLSearchParams(prev); Object.entries(patch).forEach(([k, v]) => (!v || v === "all" || v === "alle" || v === "prisliste" ? n.delete(k) : n.set(k, v))); return n; }, { replace: true });

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey || (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable))) return;
      if (searchRef.current) { e.preventDefault(); searchRef.current.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => { if (fag && view === "fag") requestAnimationFrame(() => document.getElementById(`fag-${fag}`)?.scrollIntoView({ behavior: "smooth", block: "start" })); }, [fag, view]);

  const lines = useMemo(() => (data?.lines || []).filter((l) => (g === "all" || l.group_key === g) && matchKind(l, kind) && matchLine(l, q.trim())), [data, g, kind, q]);
  const groups = useMemo(() => (data?.groups || []).map((grp) => ({ ...grp, rows: lines.filter((l) => l.group_key === grp.key) })).filter((grp) => grp.rows.length), [data, lines]);
  const selected = data?.lines.find((l) => l.id === lineId) || null;
  const forceOpen = Boolean(q.trim() || kind !== "alle" || g !== "all");
  const isOpen = (key) => forceOpen || (sections[key] ?? true);
  const toggle = (key, cur) => setSections((s) => { const next = { ...s, [key]: !cur }; localStorage.setItem(SECTIONS_KEY, JSON.stringify(next)); return next; });
  const t = data?.totals;
  const kinds = t?.kinds || {};
  const meta = data ? [data.pricelist?.valid_from ? `Katalog gyldig fra ${longDate(data.pricelist.valid_from)}` : null, `satsversjon v${data.version.version}`, t?.cases ? `brukt i ${t.cases} ${t.cases === 1 ? "sak" : "saker"}` : null].filter(Boolean).join(" · ") : null;

  return (
    <div className="mx-auto w-full max-w-[1180px] px-5 pb-20 pt-5 sm:px-8" data-testid="products-page">
      <PageBar title="Produkter" meta={meta} testId="products-bar">
        <Segment value={view} onChange={(k) => setParam({ view: k, l: null, fag: null })} items={VIEWS} label="Visning" testId="products-view" />
      </PageBar>

      {!data ? <div className="mt-6 space-y-3"><Skeleton className="h-10" /><Skeleton className="h-40" /></div> : null}

      {data && view === "prisliste" ? (
        <>
          <div className="sticky top-0 z-20 -mx-5 mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-black/[0.06] bg-white/92 px-5 py-2 backdrop-blur-md sm:-mx-8 sm:px-8" data-testid="products-toolbar">
            <QuietSearch value={q} onChange={(v) => setParam({ q: v || null })} placeholder="Søk i katalogen …" ariaLabel="Søk i katalogen" inputRef={searchRef} className="min-w-[220px] flex-1 text-[14.5px] sm:max-w-[420px]" testId="products-search-input" />
            <div className="ml-auto"><Chips value={kind} onChange={(k) => setParam({ kind: k })} items={KIND_FILTERS.map(([k, l]) => [k, l, k !== "alle" ? kinds[k] : null])} label="Prisform" testId="products-kind" /></div>
            {g !== "all" ? <button type="button" onClick={() => setParam({ g: null })} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[hsl(var(--brand)/0.08)] px-3 text-[12.5px] font-medium text-[hsl(var(--brand-strong))]" data-testid="products-group-clear">{data.groups.find((x) => x.key === g)?.label}<X className="h-3 w-3" /></button> : null}
          </div>

          <div className="-mx-4 mt-2" data-testid="products-groups">
            {groups.map((grp, gi) => {
              const open = isOpen(grp.key);
              return (
                <div key={grp.key} id={`gruppe-${grp.key}`} className={cn(gi > 0 && "mt-7")} data-testid={`products-group-${grp.key}`} data-open={open ? "1" : "0"}>
                  <SectionHead label={grp.label} summary={groupSummary(grp)} open={open} onToggle={() => toggle(grp.key, open)} testId={`products-group-toggle-${grp.key}`} />
                  {open ? <div className="border-t border-black/[0.08]">{grp.key === "timepriser" ? <RateFamilies lines={grp.rows} selected={lineId} onOpen={(id) => setParam({ l: id })} mobile={mobile} /> : <LineRows lines={grp.rows} selected={lineId} onOpen={(id) => setParam({ l: id })} />}</div> : <div className="border-t border-black/[0.06]" />}
                </div>
              );
            })}
            {!lines.length ? <p className="py-12 text-center text-[14px] text-muted-foreground" data-testid="products-no-match">{q ? <>Ingen linjer matcher «{q}».</> : "Ingen linjer i dette utvalget."}</p> : null}
          </div>
          {data.pricelist?.general_terms ? (
            <details className="mt-10 rounded-[12px] border border-black/[0.06] px-5 py-3 text-[13.5px]" data-testid="products-terms">
              <summary className="cursor-pointer font-medium text-foreground/85">Generelle betingelser i prislisten</summary>
              <p className="mt-2 whitespace-pre-line leading-relaxed text-muted-foreground">{data.pricelist.general_terms}</p>
            </details>
          ) : null}
        </>
      ) : null}

      {data && view === "fag" ? <AreasView catalog={data} focus={fag} onGroup={(gk) => setParam({ view: "prisliste", g: gk, fag: null })} /> : null}

      {data ? <LineSheet line={selected} catalog={data} onClose={() => setParam({ l: null })} onOpen={(id) => setParam({ l: id })} onGoArea={(key) => setParam({ view: "fag", fag: key, l: null })} /> : null}
    </div>
  );
}
